// US 10-digit phone-number helpers. Ported from ECU_Database
// frontend/utils.js:1040-1095 (vanilla JS) to TypeScript. Same
// conventions:
//   - Display: "(###) ###-####"
//   - Storage: 10 raw digits (no formatting characters)
//   - International numbers are out of scope; everything is US-only

/** Strip every non-digit character. Returns "" for null/undefined. */
export function digitsOnly(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/\D+/g, '');
}

/**
 * Format a phone string for display as "(555) 123-4567". If the input
 * doesn't reduce to exactly 10 digits, the original string is echoed
 * back so legacy / partial values stay visible while users clean them up.
 * Returns "—" for null / empty.
 */
export function formatPhoneDisplay(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '—';
  const d = digitsOnly(raw);
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  // 11 digits starting with 1 — accept and drop the country code.
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return raw;
}

/**
 * Normalize a typed phone for storage: returns the 10-digit string
 * (e.g. "5551234567") if exactly 10 digits are present, otherwise null.
 * Used by form submit handlers to decide whether to send the field at
 * all — null means "leave it blank, don't store a junk value".
 */
export function stripPhoneForStorage(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const d = digitsOnly(raw);
  if (d.length === 10) return d;
  if (d.length === 11 && d.startsWith('1')) return d.slice(1);
  return null;
}

/**
 * Live-format a partial input as the user types. Used by the
 * <PhoneInput> component on every keystroke. Builds the formatted
 * string from however many digits the user has entered so far:
 *   "5"          → "(5"
 *   "555"        → "(555) "
 *   "5551234"    → "(555) 123-4"
 *   "5551234567" → "(555) 123-4567"
 * Anything beyond 10 digits is silently truncated.
 */
export function formatPhoneAsTyped(raw: string): string {
  const d = digitsOnly(raw).slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
